import { currentDate_dmy, CurrentTime, isFoodERPDotIn, loginCompanyName, loginUserDetails } from "../../components/Common/CommonFunction";
import { numberWithCommas } from "../Report_common_function";
import * as table from "./Challantabledata";

/**
 * ChallanReportStyle.js — RA Masale "Delivery challan" format
 * ------------------------------------------------------------------
 * All doc.* drawing calls live here, same split as your existing
 * ReportStyle.js. Page.js wires these together in order.
 * ------------------------------------------------------------------
 *
 * UPDATED against your real API response: `data.Party*` (PartyName /
 * PartyGSTIN / PartyAddress) already carries a full seller-side
 * company profile per transaction (GSTIN, FSSAI, State, address) —
 * so the letterhead below now prefers those live values over the
 * generic loginUserDetails()/loginCompanyName() fallback, which only
 * kicks in if the response doesn't send them. An explicit
 * `companyDetails` param, if you pass one, always wins over both.
 */

const getLoginCompanyDetails = () => {
    const user = loginUserDetails() || {};
    const companyName = loginCompanyName();

    return {
        GSTIN: user.GSTIN || "",
        Name: companyName || "",
        AddressLine1: user.PartyAddress || "",
    };
};

/**
 * Resolves the seller letterhead in priority order:
 *   1. explicit `companyDetails` param (always wins)
 *   2. live values off the transaction response (`data.Party*`)
 *   3. logged-in company profile (loginUserDetails/loginCompanyName)
 */
export const resolveCompanyDetails = (data, companyDetails) => {
    const loginDefaults = getLoginCompanyDetails();

    return {
        ...loginDefaults,
        GSTIN: data.PartyGSTIN || loginDefaults.GSTIN,
        Name: data.PartyName || loginDefaults.Name,
        AddressLine1: data.PartyAddress || loginDefaults.AddressLine1,
        ...(companyDetails || {}),
    };
};

export const pageBorder = (doc) => {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.75);
    doc.rect(30, 20, 540, 795);
    doc.setLineWidth(0.5);
};

const drawTightLabelValue = (doc, label, value, x, y, { boldLabel = true, gap = 4 } = {}) => {
    doc.setFont("helvetica", boldLabel ? "bold" : "normal");
    doc.text(`${label} :`, x, y);
    const labelWidth = doc.getTextWidth(`${label} :`);

    doc.setFont("helvetica", "normal");
    const valueText = ` ${value ?? ""}`;
    doc.text(valueText, x + labelWidth + gap, y);
    const valueWidth = doc.getTextWidth(valueText);

    return x + labelWidth + gap + valueWidth;
};

/**
 * Header: top-left GSTIN, centered underlined "Delivery challan"
 * title, large company name, then the two address lines (main +
 * branch) centered underneath — matches the sample exactly, no
 * "TAX INVOICE" / "Original Copy" text since those are invoice-only.
 */
export const pageHeder = (doc, data, companyDetails) => {
    const company = resolveCompanyDetails(data, companyDetails);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(`GSTIN :  ${company.GSTIN}`, 38, 34);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    const title = "Delivery challan";
    doc.text(title, 300, 34, { align: "center" });
    const titleWidth = doc.getTextWidth(title);
    doc.setLineWidth(0.75);
    doc.line(300 - titleWidth / 2, 36.5, 300 + titleWidth / 2, 36.5);
    doc.setLineWidth(0.5);

    doc.setFontSize(20);
    doc.text(company.Name, 300, 55, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text(company.AddressLine1, 300, 68, { align: "center" });

    doc.setDrawColor(0, 0, 0);
    doc.line(30, 78, 570, 78);
};

/**
 * Two-column block: Party Details (the receiver — Customer*) on the
 * left, Challan meta info (Challan No / Dated / Driver Name /
 * Vehicle No / Trip No) on the right — direct doc.text calls, same
 * reasoning as reportPartyAndInvoiceInfo() in the invoice style
 * file: exact control over wrapping and line spacing.
 */
export const reportPartyAndChallanInfo = (doc, data) => {
    const startY = 90;
    const leftX = 34;
    const rightX = 305;
    const lineH = 13;

    const party = table.PartyDetails(data);
    let y = startY;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Party Details :", leftX, y);
    y += lineH;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(party.name, leftX, y);
    y += lineH;

    doc.setFont("helvetica", "normal");
    if (party.refLine) {
        doc.text(party.refLine, leftX, y);
        y += lineH;
    }

    if (party.altName) {
        doc.text(party.altName, leftX, y);
        y += lineH;
    }

    const wrappedAddress = doc.splitTextToSize(party.address, 250);
    doc.text(wrappedAddress, leftX, y);
    y += wrappedAddress.length * lineH;

    if (party.gstinLine) {
        doc.text(party.gstinLine, leftX, y);
        y += lineH;
    }

    const leftBottomY = y;

    // ---- right column: Challan Info (tight label:value spacing) ----
    doc.setFontSize(9);
    const infoRows = table.ChallanInfoRows(data);
    let ry = startY;

    infoRows.forEach((row) => {
        drawTightLabelValue(doc, row.label, row.value, rightX, ry);
        ry += lineH;
    });

    const bottomY = Math.max(leftBottomY, ry) + 6;

    doc.setDrawColor(0, 0, 0);
    doc.line(30, bottomY, 570, bottomY);
    doc.line(300, startY - 10, 300, bottomY);

    return bottomY;
};

/**
 * The one-line "We are pleased to place the order for the following
 * items :" caption that sits between the party box and the items
 * table on the sample.
 */
export const reportOrderingText = (doc, startY) => {
    const y = startY + 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("We are pleased to place the order for the following items :", 34, y);
    return y + 8;
};

export const tableBody = (doc, data, startY) => {
    const w = table.columnWidths();
    const cols = table.columns();

    const options = {
        margin: { left: 30, right: 30, bottom: 140 },
        theme: "grid",
        startY,
        tableWidth: "wrap",
        headStyles: {
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            fontStyle: "bold",
            halign: "center",
            fontSize: 9,
            lineColor: [0, 0, 0],
            lineWidth: 0.5,
        },
        bodyStyles: {
            fontSize: 9,
            textColor: [20, 20, 20],
            lineColor: [0, 0, 0],
            lineWidth: 0.5,
            cellPadding: 4,
            overflow: "linebreak",
        },
        columnStyles: Object.fromEntries(
            cols.map((_, i) => [i, { cellWidth: w[i], halign: i === 1 ? "left" : "center" }])
        ),
    };

    doc.autoTable(cols, table.Rows(data), options);
    return doc.previousAutoTable.finalY;
};

/**
 * Footer: single "Grand Total ... Units" row (no CGST/SGST/Round-Off
 * — a delivery challan carries no pricing), followed by the
 * "For <Company Name>" / "Receiver's Sign & Stamp" / "Authorised
 * Signatory" signature block, laid out to match the sample.
 */
export const reportFooterTotals = (doc, data, startY, companyDetails) => {
    const company = resolveCompanyDetails(data, companyDetails);

    const { totalQty = 0, totalQtyInNo = 0 } = data.footerTotals || {};

    let y = startY + 18;

    doc.setDrawColor(0, 0, 0);
    doc.line(30, startY + 4, 570, startY + 4);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    if (isFoodERPDotIn()) {
        doc.text("Grand Total", 64, y);
        doc.text(String(totalQtyInNo), 460, y, { align: "center" });
        doc.text("Units", 565, y, { align: "right" });
    } else {
        doc.text("Grand Total", 300, y);
        doc.text(numberWithCommas(Number(totalQty).toFixed(2)), 465, y, { align: "right" });
        doc.text("Units", 560, y, { align: "right" });
    }

    doc.line(30, y + 10, 570, y + 10);

    y += 55;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(`For ${company.Name}`, 560, y - 30, { align: "right" });

    y += 34;
    doc.setFont("helvetica", "normal");
    doc.text("Receiver's Sign & Stamp", 34, y);
    doc.setFont("helvetica", "bold");
    doc.text("Authorised Signatory", 560, y, { align: "right" });

    return y;
};

export const pageFooter = (doc) => {
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        pageBorder(doc)
        doc.text(`Page ${i} of ${pageCount}`, 520, 825);
        doc.text(
            `Print Date :${String(currentDate_dmy)} Time ${String(CurrentTime())}`,
            34,
            825
        );
    }
};